import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Zap, Truck, Shield, Award, MessageCircle, Globe, Undo2, Smartphone, Laptop, Headphones, Gamepad2, Watch, Cable } from "lucide-react";
import { Button } from "@/components/ui/button";

const ICONS = { smartphone: Smartphone, laptop: Laptop, headphones: Headphones, "gamepad-2": Gamepad2, watch: Watch, cable: Cable };

export default function Home() {
  const { t } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/ads", { params: { placement: "home_top" } }).then((r) => setAds(r.data));
  }, []);

  return (
    <div>
      {ads[0] && (
        <div className="bg-primary text-primary-foreground" data-testid="active-banner">
          <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-3">
              <Zap className="w-4 h-4" />
              <span className="font-semibold">{ads[0].title}</span>
              <span className="hidden sm:inline opacity-90">— {ads[0].subtitle}</span>
            </div>
            <Link to={ads[0].button_link} className="underline underline-offset-4 hover:opacity-80" data-testid="banner-cta">
              {ads[0].button_text} →
            </Link>
          </div>
        </div>
      )}

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-70">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-4 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-background/50 backdrop-blur text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              {t("hero.badge")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
              {t("hero.title.a")}<br />
              <span className="gradient-text">{t("hero.title.b")}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild size="lg" className="rounded-full btn-primary text-base px-8" data-testid="hero-view-products">
                <Link to="/produtos">{t("cta.viewProducts")} <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full text-base px-8" data-testid="hero-view-novidades">
                <Link to="/novidades">{t("cta.explore")}</Link>
              </Button>
            </div>
            <div className="flex gap-8 pt-6 text-sm">
              <div><div className="font-display font-bold text-2xl">50k+</div><div className="text-muted-foreground text-xs">Clientes</div></div>
              <div><div className="font-display font-bold text-2xl">4.8★</div><div className="text-muted-foreground text-xs">Rating</div></div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-2xl">
              <img src="https://images.unsplash.com/photo-1615655406736-b37c4fabf923?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="VOLTIX Tech" className="w-full h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-80">{t("hero.featured")}</div>
                  <div className="font-display text-xl font-bold">Voltix Neo 15 Pro</div>
                </div>
                <Button asChild size="sm" className="rounded-full btn-primary">
                  <Link to="/produto/voltix-neo-15-pro">{t("cta.viewProduct")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t("home.categories.eyebrow")}</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold">{t("home.categories.title")}</h2>
          </div>
          <Link to="/produtos" className="text-sm text-primary hover:underline" data-testid="cats-see-all">{t("cta.viewAll")} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => {
            const Icon = ICONS[c.icon] || Zap;
            return (
              <Link key={c.id} to={`/produtos?categoria=${c.slug}`} className="group rounded-2xl border border-border bg-card p-6 flex flex-col items-center gap-3 card-hover hover:border-primary/40 hover:shadow-lg" data-testid={`category-${c.slug}`}>
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Icon className="w-6 h-6" />
                </div>
                <span className="font-medium text-sm text-center">{c.name}</span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t("home.featured.eyebrow")}</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold">{t("home.featured.title")}</h2>
          </div>
          <Link to="/produtos" className="text-sm text-primary hover:underline" data-testid="products-see-all">{t("cta.viewAll")} →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:h-[520px]">
          <div className="lg:col-span-2 relative rounded-3xl overflow-hidden group border border-border">
            <img src="https://images.unsplash.com/photo-1763162603999-8a1958b13cf1?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80" alt="Nova geração" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/40 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white space-y-4">
              <div className="text-xs uppercase tracking-widest opacity-80">{t("home.bento.eyebrow")}</div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-tight whitespace-pre-line">{t("home.bento.title")}</h3>
              <Button asChild size="lg" className="rounded-full btn-primary" data-testid="descobrir-agora">
                <Link to="/produtos">{t("cta.discover")} <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
          <div className="grid grid-rows-2 gap-4">
            <Link to="/produtos?categoria=gaming" className="relative rounded-3xl overflow-hidden group border border-border">
              <img src="https://images.unsplash.com/photo-1696710257827-75e2e5954059?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" alt="Gaming" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute bottom-6 left-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Gaming</div>
                <div className="font-display font-bold text-2xl">{t("home.bento.gaming")}</div>
              </div>
            </Link>
            <Link to="/produtos?categoria=audio" className="relative rounded-3xl overflow-hidden group border border-border">
              <img src="https://images.unsplash.com/photo-1515940175183-6798529cb860?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" alt="Áudio" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute bottom-6 left-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Audio</div>
                <div className="font-display font-bold text-2xl">{t("home.bento.audio")}</div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {[
            { Icon: Truck, title: t("trust.delivery"), text: t("trust.deliveryText") },
            { Icon: Shield, title: t("trust.secure"), text: t("trust.secureText") },
            { Icon: Award, title: t("trust.curated"), text: t("trust.curatedText") },
            { Icon: MessageCircle, title: t("trust.support"), text: t("trust.supportText") },
            { Icon: Undo2, title: t("trust.returns"), text: t("trust.returnsText") },
            { Icon: Globe, title: t("trust.currency"), text: t("trust.currencyText") },
          ].map((tt, i) => (
            <div key={i} className="rounded-2xl border border-border p-6 text-center bg-card card-hover" data-testid={`trust-${i}`}>
              <tt.Icon className="w-6 h-6 mx-auto mb-3 text-primary" />
              <div className="font-display font-semibold text-sm">{tt.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{tt.text}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
